import { ArrowRightIcon } from '@heroicons/react/24/solid'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'
import { CreateSourceInput, Source, SourceType } from '@ui/__generated__/graphql'
import { Button } from '@ui/components/core'
import { sourceTypeConfigs } from '@ui/constants/onboarding'
import { sourceTypeToLogoAssetSlug } from '@ui/constants/sources'
import useAppNavigation from '@ui/lib/hooks/useAppNavigation'
import { useDemoNavigation } from '@ui/lib/hooks/useDemoNavigation'
import { useLogoPathBySlug } from '@ui/lib/hooks/useLogoPathBySlug'
import { uuid } from '@ui/lib/utils'
import { needsDatableApiKey } from '@ui/lib/utils/sources'
import { CreateSourceQuery } from '@ui/loaders/sources'
import { useAuth } from '@ui/providers/AuthProvider'
import { FeatureFlagName, useIsFeatureEnabled } from '@ui/providers/FeatureFlagProvider'
import { RouteNamesEnum } from '@ui/router/routes'
import clsx from 'clsx'
import * as Yup from 'yup'

import { DetectSource, SelectableSourceType } from './hooks/useOnboarding'

const POLL_SOURCE_INTERVAL = 3000
const POLL_INTERVAL_ATTEMPTS = 5 // 5 * 3000 = 15s max duration

const sourceTypeGraphQLToLabel: Record<SourceType, string> = {
  [SourceType.AwsLambda]: 'AWS Lambda',
  [SourceType.Datadog]: 'Datadog',
  [SourceType.Fluentbit]: 'Fluentbit',
  [SourceType.NewRelic]: 'New Relic',
  [SourceType.Otel]: 'OpenTelemetry',
  [SourceType.Vercel]: 'Vercel',
  [SourceType.CatchAll]: 'Catch All',
}

type SourceCardProps = {
  className?: string
  logoPathBySlug: Record<string, string>
  onClick?: () => void
  source: SourceType
}

const SourceCard = ({ className, logoPathBySlug, onClick, source }: SourceCardProps) => {
  return (
    <div className={clsx(className, 'cursor-pointer rounded-md border border-gray-200 p-4')} onClick={onClick}>
      <div className="flex h-7 items-center gap-2">
        <img src={logoPathBySlug[sourceTypeToLogoAssetSlug[source]]} alt={source} className="h-7 w-7" />
        <p className="flex-1 text-xs">{sourceTypeGraphQLToLabel[source]}</p>
      </div>
    </div>
  )
}

type SourceTypeSelectionProps = {
  selectedSourceType: SelectableSourceType | null
  onSelectSourceType: (sourceType: SelectableSourceType) => void
  onNext: () => void
  onBack: () => void
}

const sourceTypeSlugs = Object.values(SourceType).map((sourceType) => sourceTypeToLogoAssetSlug[sourceType])

export const SourceTypeSelection = ({
  selectedSourceType,
  onSelectSourceType,
  onNext,
  onBack,
}: SourceTypeSelectionProps) => {
  const logoPathBySlug = useLogoPathBySlug(sourceTypeSlugs)

  return (
    <>
      <div className="flex flex-1 justify-center">
        <div className="h-full max-w-[800px] overflow-y-scroll">
          <div className="flex h-full items-center justify-center">
            <div className="flex flex-col gap-4">
              <h1 className="text-center text-4xl font-semibold">Select Source</h1>
              <p className="text-center text-gray-600">What tool are you using to instrument your data?</p>
              <p className="text-center text-gray-600">Choose one, you can add more later.</p>
              <div className="flex min-w-[600px] flex-col gap-2">
                {Object.values(SourceType).map((sourceType) => {
                  if (sourceType === SourceType.CatchAll) return null

                  const isSelected = sourceType === selectedSourceType

                  return (
                    <SourceCard
                      key={sourceType}
                      className={clsx({
                        'border-primary-300 bg-gray-50': isSelected,
                        'hover:bg-gray-50': !isSelected,
                      })}
                      logoPathBySlug={logoPathBySlug}
                      onClick={() => onSelectSourceType(sourceType)}
                      source={sourceType}
                    />
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer onNext={onNext} nextDisabled={!selectedSourceType} onBack={onBack} />
    </>
  )
}

type SourceConfigurationProps = {
  selectedSourceType: SelectableSourceType
  onBack: () => void
  onNext: (apiKey: string) => void
}

export const SourceConfiguration = ({ selectedSourceType, onNext, onBack }: SourceConfigurationProps) => {
  const createSourceAndLastSeenEnabled = useIsFeatureEnabled(FeatureFlagName.CreateSourceAndLastSeen)

  const [createSource] = CreateSourceQuery()

  const schema = Yup.object({
    // an API key should either be given to us for the particular source or we generate one
    apiKey: Yup.string().required('API Key is required'),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      apiKey: '',
    },
  })

  const logoPathBySlug = useLogoPathBySlug([sourceTypeToLogoAssetSlug[selectedSourceType]])

  const shouldGenerateApiKey = needsDatableApiKey(selectedSourceType)

  const onSubmit = async () => {
    const isValid = await trigger()

    if (isValid) {
      const { apiKey } = getValues()
      onNext(apiKey)
    }
  }

  useEffect(() => {
    if (shouldGenerateApiKey) {
      const apiKey = uuid()

      setValue('apiKey', apiKey)

      // creates the source as we give the user the instructions on how to send data to us for this source type
      // if the user sends us data before we create the source, then a source will have been automatically created
      // of a type based on the endpoint the data is sent to rather than based on the selectedSourceType
      if (createSourceAndLastSeenEnabled) {
        const input: CreateSourceInput = {
          apiKey,
          type: selectedSourceType,
        }

        createSource({ variables: { input } })
      }
    }
  }, [shouldGenerateApiKey])

  const apiKey = watch('apiKey')

  const { currentInstance } = useAuth()
  const instanceUrl = currentInstance?.name ? `${currentInstance.name}.dtbl.io` : 'YOUR_DATABLE_HOST.dtbl.io'

  return (
    <div className="flex flex-col">
      <div className="flex flex-grow items-center justify-center overflow-y-auto">
        <div className="max-w-[800px]">
          <div className="flex max-h-full items-center justify-center">
            <div className="flex w-full flex-col gap-4 pt-32">
              <h1 className="text-center text-4xl font-semibold">Configure Your Source</h1>
              <p className="text-center text-gray-600">
                Follow the steps below to configure your source. Copy the code provided and paste it in your
                configuration.
              </p>
              <SourceCard logoPathBySlug={logoPathBySlug} source={selectedSourceType} />
              <div className="pb-32">
                <>
                  {sourceTypeConfigs[selectedSourceType].instructions({ datableApiKey: apiKey, instanceUrl })}
                  {!shouldGenerateApiKey && (
                    <form className="my-2 flex flex-col gap-2" onSubmit={handleSubmit(onSubmit)}>
                      <div className="flex flex-col gap-2">
                        <label className="text-sm text-gray-800">API Key</label>
                        <input
                          type="text"
                          className="h-12 rounded border-0 px-4 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          placeholder="abcd1234"
                          {...register('apiKey')}
                        />
                        {errors['apiKey'] && (
                          <p className="mt-2 text-xs italic text-red-500">{errors['apiKey'].message}</p>
                        )}
                      </div>
                    </form>
                  )}
                </>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer onNext={onSubmit} nextDisabled={false} onBack={onBack} />
    </div>
  )
}

type SettingUpSourceProps = {
  onFailure: () => void
  onSuccess: (source: Source) => void
  onBack: () => void
  detectSource: DetectSource
}

export const SettingUpSource = ({ detectSource, onBack, onFailure, onSuccess }: SettingUpSourceProps) => {
  const pollAttemptsRef = useRef(0)

  useEffect(() => {
    const timeout = setInterval(async () => {
      if (pollAttemptsRef.current >= POLL_INTERVAL_ATTEMPTS) {
        onFailure()
        return
      }

      await detectSource(
        (source) => {
          onSuccess(source)
          clearInterval(timeout)
        },
        () => {
          pollAttemptsRef.current++
        }
      )
    }, POLL_SOURCE_INTERVAL)

    return () => clearInterval(timeout)
  }, [detectSource, onFailure, onSuccess])

  return (
    <>
      <div className="flex flex-1 justify-center">
        <div className="h-full max-w-[800px] overflow-y-scroll">
          <div className="flex h-full items-center justify-center">
            <div className="flex flex-col gap-4">
              <ConnectingDots animate />
              <h1 className="text-center text-4xl font-semibold">Setting up Your Source</h1>
              <p className="text-center text-gray-600">
                We’re currently setting up your data source. Please hang tight while we connect and wait to receive your
                data...
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer nextDisabled={true} onBack={onBack} />
    </>
  )
}

export const ConfigurationSuccessful = ({ onboardablePipelineId }: { onboardablePipelineId?: string }) => (
  <>
    <div className="flex flex-1 justify-center">
      <div className="h-full max-w-[800px] overflow-y-scroll">
        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col gap-4">
            <ConnectingDots />
            <h1 className="text-center text-4xl font-semibold">Configuration Successful!</h1>
            <p className="text-center text-gray-600">
              Your data source has been successfully connected. You’re now ready to start leveraging Datable to manage
              and transform your telemetry data.
            </p>
            <GoToDatable pipelineId={onboardablePipelineId} />
          </div>
        </div>
      </div>
    </div>
  </>
)

export const ConfigurationUnsuccessful = ({ onBack }: { onBack: () => void }) => (
  <>
    <div className="flex flex-1 justify-center">
      <div className="h-full max-w-[800px] overflow-y-scroll">
        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col gap-4">
            <ConnectingDots />
            <h1 className="text-center text-4xl font-semibold">Hmmm this is taking longer than expected...</h1>
            <p className="text-center text-gray-600">While we sort this out, we’re sending you to Datable.</p>
            <GoToDatable />
          </div>
        </div>
      </div>
    </div>
    <Footer nextDisabled onBack={onBack} />
  </>
)

export const StartTransforming = ({ onBack }: { onBack: () => void }) => (
  <>
    <div className="flex flex-1 justify-center">
      <div className="h-full max-w-[800px] overflow-y-scroll">
        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col gap-4">
            <ConnectingDots />
            <h1 className="text-center text-4xl font-semibold">Start transforming your data!</h1>
            <p className="text-center text-gray-600">
              You’re now ready to start leveraging Datable to manage and transform your telemetry data.
            </p>
            <GoToDatable />
          </div>
        </div>
      </div>
      <Footer nextDisabled onBack={onBack} />
    </div>
  </>
)

const GoToDatable = ({ pipelineId }: { pipelineId?: string }) => {
  const { navigateTo } = useAppNavigation()
  const { currentInstance } = useAuth()

  const navigate = () => {
    if (pipelineId) {
      navigateTo(RouteNamesEnum.EXISTING_PIPELINE, { pipelineId, instanceId: currentInstance?.id })
    } else {
      navigateTo(RouteNamesEnum.EVENTS_EXPLORER)
    }
  }

  return (
    <div className="flex justify-center">
      <Button onClick={navigate}>Go to Datable</Button>
    </div>
  )
}

const ConnectingDots = ({ animate = false }: { animate?: boolean }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (animate) {
      const interval = setInterval(() => {
        setCount((count) => (count + 1) % 3)
      }, 1000)

      return () => {
        clearInterval(interval)
      }
    }
  }, [])

  return (
    <div className="mb-8 flex justify-center">
      <div className="flex gap-2">
        <div
          className={clsx(
            'h-4 w-4 rounded-full transition duration-300 ease-in-out',
            animate && count === 0 ? 'bg-primary-500' : 'bg-gray-200'
          )}
        />
        <div
          className={clsx(
            'h-4 w-4 rounded-full transition duration-300 ease-in-out',
            !animate || (animate && count === 1) ? 'bg-primary-500' : 'bg-gray-200'
          )}
        />
        <div
          className={clsx(
            'h-4 w-4 rounded-full transition duration-300 ease-in-out',
            animate && count === 2 ? 'bg-primary-500' : 'bg-gray-200'
          )}
        />
      </div>
    </div>
  )
}

type FooterProps = {
  onBack?: () => void
  onNext?: () => void
  backDisabled?: boolean
  nextDisabled: boolean
}

const Footer = ({ onBack, onNext, nextDisabled, backDisabled = false }: FooterProps) => {
  const handleDemoNavigation = useDemoNavigation()

  return (
    <footer className="sticky bottom-0 flex h-16 w-full items-center border-t border-gray-200 bg-white px-4">
      <p
        className="flex-1 cursor-pointer text-base font-medium text-gray-600 underline transition-all duration-300 ease-in-out hover:text-primary-500"
        onClick={handleDemoNavigation}>
        Skip setup and go to Sandbox
      </p>
      <div className="flex gap-2">
        <Button variant="tertiary" disabled={backDisabled} onClick={onBack}>
          Back
        </Button>
        <Button
          onClick={onNext}
          icon={<ArrowRightIcon className="h-4 w-4" />}
          iconPosition="right"
          disabled={nextDisabled}>
          Next
        </Button>
      </div>
    </footer>
  )
}
